/*
 * section-recipe.js — scoped basket handler for the reusable recipe section.
 *
 * Deliberately avoids the theme/app hooks that product pages use
 * (data-product-form, .product-form, data-add-to-cart), which caused the
 * hidden id/quantity inputs to be cleared and Cowlendar to rewrite the forms.
 *
 * Posts to the theme's cart route using the same multiple-item FormData
 * contract the theme's own bulk-add form uses (items[N][id] / items[N][quantity]),
 * then refreshes the cart through the theme's exposed window.cart instance.
 *
 * TRACKING (added 14 Oct 2026): on a successful add, fires a standard GA4
 * `add_to_cart` dataLayer event (with ecommerce.items + recipe context) and,
 * on a recipe -> product link click, a `select_item` event. This closes the
 * previously unmeasured recipe-to-basket step. All pushes are best-effort and
 * wrapped so a missing dataLayer can never break the cart.
 */
(function () {
  'use strict';

  var SECTION_SELECTOR = '[data-section-type="recipe"]';
  var busySections = new WeakSet();

  function init() {
    var sections = document.querySelectorAll(SECTION_SELECTOR);
    if (!sections.length) return;
    sections.forEach(bindSection);
  }

  function bindSection(section) {
    if (section.recipeBasketBound) return;
    section.recipeBasketBound = true;
    section.querySelectorAll('[data-recipe-add-form]').forEach(function (form) {
      form.addEventListener('submit', onIndividualSubmit);
    });

    var addAll = section.querySelector('[data-recipe-add-all]');
    if (addAll) {
      addAll.addEventListener('click', onAddAll);
    }

    bindProductClicks(section);
  }

  function bindProductClicks(section) {
    section.querySelectorAll('a[href*="/products/"]').forEach(function (link) {
      if (link.recipeProductClickBound) return;
      link.recipeProductClickBound = true;
      link.addEventListener('click', function () {
        var ctx = recipeContext(section);
        var itemName = (link.getAttribute('aria-label') || link.textContent || '').trim();
        var href = link.getAttribute('href') || '';
        var itemId = href.split('/products/')[1] || href;
        pushEvent('select_item', {
          ecommerce: {
            currency: 'GBP',
            item_list_name: 'recipe',
            items: [{ item_id: itemId, item_name: itemName || itemId }]
          },
          recipe_handle: ctx.recipe_handle,
          recipe_title: ctx.recipe_title
        });
      });
    });
  }

  function cartAddUrl() {
    return (window.theme && window.theme.routes && window.theme.routes.cart_add_url) || '/cart/add.js';
  }

  function integer(value, allowZero) {
    var text = String(value == null ? '' : value).trim();
    if (!/^\d+$/.test(text)) return null;
    var number = Number(text);
    return Number.isSafeInteger(number) && number >= (allowZero ? 0 : 1) ? number : null;
  }

  function collectItems(forms) {
    var grouped = new Map();
    forms.forEach(function (form) {
      var id = String(form.getAttribute('data-recipe-variant-id') || '').trim();
      var quantity = integer(form.getAttribute('data-recipe-quantity'), false);
      var price = integer(form.getAttribute('data-recipe-unit-price'), true);
      var title = (form.getAttribute('data-recipe-product-title') || '').trim();
      if (!integer(id, false) || quantity === null || price === null) throw new Error('Invalid recipe item');
      var existing = grouped.get(id);
      if (existing && existing.price !== price) throw new Error('Inconsistent recipe price');
      var combined = quantity + (existing ? existing.quantity : 0);
      if (!Number.isSafeInteger(combined) || !Number.isSafeInteger(price * combined)) throw new Error('Invalid recipe total');
      grouped.set(id, { id: id, quantity: combined, price: price, title: title });
    });
    return Array.from(grouped.values()).sort(function (a, b) { return a.id.localeCompare(b.id); });
  }

  function validateSummary(section, items) {
    var summary = section.querySelector('[data-recipe-total-cents]');
    if (!summary || !items.length) throw new Error('Missing recipe summary');
    var total = items.reduce(function (sum, item) { return sum + item.price * item.quantity; }, 0);
    var units = items.reduce(function (sum, item) { return sum + item.quantity; }, 0);
    if (!Number.isSafeInteger(total) || total !== integer(summary.getAttribute('data-recipe-total-cents'), true) ||
        units !== integer(summary.getAttribute('data-recipe-item-count'), false) ||
        items.length !== integer(summary.getAttribute('data-recipe-product-count'), false)) {
      throw new Error('Recipe summary changed');
    }
  }

  function setBusy(el, busy) {
    if (busy) {
      el.setAttribute('aria-busy', 'true');
      el.classList.add('is-loading');
    } else {
      el.removeAttribute('aria-busy');
      el.classList.remove('is-loading');
    }
  }

  function statusMessage(section, type, text) {
    var el = section.querySelector('.recipe-status');
    if (!el) return;
    el.textContent = text;
    el.classList.remove('recipe-status--success', 'recipe-status--error');
    el.classList.add(type === 'error' ? 'recipe-status--error' : 'recipe-status--success');
    el.classList.add('is-visible');
    el.setAttribute('role', type === 'error' ? 'alert' : 'status');
  }

  function hideStatus(section) {
    var el = section.querySelector('.recipe-status');
    if (el) el.classList.remove('is-visible');
  }

  // ---------- tracking helpers ----------

  function recipeContext(section) {
    return {
      recipe_handle: (section.getAttribute('data-recipe-handle') || '').trim(),
      recipe_title: (section.getAttribute('data-recipe-title') || '').trim()
    };
  }

  function gtagItems(items) {
    return items.map(function (item) {
      return {
        item_id: item.id,
        item_name: item.title || item.id,
        quantity: item.quantity,
        price: item.price / 100
      };
    });
  }

  function pushEvent(eventName, payload) {
    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(Object.assign({ event: eventName }, payload));
    } catch (err) {
      /* best-effort; tracking must never break the cart */
    }
  }

  function trackAddToCart(section, items) {
    var ctx = recipeContext(section);
    var value = items.reduce(function (sum, item) { return sum + item.price * item.quantity; }, 0) / 100;
    pushEvent('add_to_cart', {
      ecommerce: {
        currency: 'GBP',
        value: value,
        item_list_name: 'recipe',
        items: gtagItems(items)
      },
      recipe_handle: ctx.recipe_handle,
      recipe_title: ctx.recipe_title
    });
  }

  // items: [{ id: variantId, quantity: n }]
  function addToCart(items, done) {
    var fd = new FormData();
    items.forEach(function (item, i) {
      fd.append('items[' + i + '][id]', item.id);
      fd.append('items[' + i + '][quantity]', String(item.quantity));
    });

    fetch(cartAddUrl(), {
      method: 'POST',
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
        'Accept': 'application/javascript'
      },
      body: fd,
      credentials: 'same-origin'
    })
      .then(function (resp) {
        if (!resp.ok) throw new Error('HTTP ' + resp.status);
        return resp.json();
      })
      .then(function (cart) {
        refreshCart();
        var priceChanged = (cart.items || []).some(function (line) {
          var submitted = items.find(function (item) { return item.id === String(line.variant_id || line.id); });
          var actualPrice = line.original_price == null ? line.price : line.original_price;
          return submitted && actualPrice != null && actualPrice !== submitted.price;
        });
        done(null, cart, priceChanged);
      }, function (err) {
        done(err);
      });
  }

  function refreshCart() {
    if (window.cart && typeof window.cart.getCart === 'function') {
      try {
        var refresh = window.cart.getCart();
        if (refresh && typeof refresh.catch === 'function') refresh.catch(function () {});
      } catch (err) {}
    }
    if (document.dispatchEvent) {
      try {
        document.dispatchEvent(new CustomEvent('theme:cart:add', { bubbles: true }));
        document.dispatchEvent(new CustomEvent('theme:cart:added', { bubbles: true }));
      } catch (err) {}
    }
  }

  function openDrawer() {
    try {
      if (window.cart && typeof window.cart.openCartDrawer === 'function') {
        window.cart.openCartDrawer();
      } else if (document.dispatchEvent) {
        document.dispatchEvent(new CustomEvent('theme:cart:open', { bubbles: true }));
      }
    } catch (err) {}
  }

  function onIndividualSubmit(e) {
    e.preventDefault();
    var form = e.currentTarget;
    var section = form.closest(SECTION_SELECTOR);
    if (!section) return;
    if (busySections.has(section)) return;
    var items;
    try {
      items = collectItems([form]);
    } catch (err) {
      statusMessage(section, 'error', section.getAttribute('data-recipe-error-text') || 'This product is currently unavailable.');
      return;
    }

    busySections.add(section);
    setBusy(form, true);
    hideStatus(section);

    addToCart(items, function (err, cart, priceChanged) {
      busySections.delete(section);
      setBusy(form, false);
      if (err) {
        statusMessage(section, 'error', section.getAttribute('data-recipe-error-text') || 'Something went wrong. Please try again.');
        return;
      }
      trackAddToCart(section, items);
      statusMessage(section, 'success', section.getAttribute(priceChanged ? 'data-recipe-price-changed-text' : 'data-recipe-success-text') || 'Added to basket.');
      openDrawer();
    });
  }

  function onAddAll(e) {
    e.preventDefault();
    var btn = e.currentTarget;
    var section = btn.closest(SECTION_SELECTOR);
    if (!section) return;
    if (busySections.has(section)) return;

    var forms = section.querySelectorAll('[data-recipe-add-form]');
    var items;
    try {
      items = collectItems(forms);
      validateSummary(section, items);
    } catch (err) {
      statusMessage(section, 'error', section.getAttribute('data-recipe-error-text') || 'Something went wrong. Please try again.');
      return;
    }

    busySections.add(section);
    setBusy(btn, true);
    hideStatus(section);

    addToCart(items, function (err, cart, priceChanged) {
      busySections.delete(section);
      setBusy(btn, false);
      if (err) {
        statusMessage(section, 'error', section.getAttribute('data-recipe-error-text') || 'Something went wrong. Please try again.');
        return;
      }
      trackAddToCart(section, items);
      statusMessage(section, 'success', section.getAttribute(priceChanged ? 'data-recipe-price-changed-text' : 'data-recipe-success-text') || 'Added to basket.');
      openDrawer();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', init);
})();
